import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import { KPICard } from "../components/KPICard";
import { RelevanceBadge, StatusBadge } from "../components/Badges";
import { Activity, AlertTriangle, FileText, Target, Database, MapPin, History, RefreshCw, CheckCircle, XCircle, Sparkles, Download } from "lucide-react";
import { toast } from "sonner";

const FmtDate = ({ value }) => {
  if (!value) return <span className="text-zinc-400">—</span>;
  const d = new Date(value);
  return (
    <span className="font-mono text-xs">
      {d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "2-digit" })}
    </span>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const navigate = useNavigate();

  const [recentRuns, setRecentRuns] = useState([]);
  const [newOpps, setNewOpps] = useState(null);

  const load = async () => {
    try {
      const [s, o, rs, n] = await Promise.all([
        api.stats(),
        api.opportunities({ active_only: true, sort: "relevance", limit: 8 }),
        api.runs(3),
        api.newOpportunities().catch(() => null),
      ]);
      setStats(s);
      setItems(o.items);
      setRecentRuns(rs);
      if (n && n.items && n.items.length > 0) setNewOpps(n);
    } catch (e) {
      console.error(e);
    }
  };

  const downloadNewCsv = async () => {
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API_BASE}/export/new-csv`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(r.statusText);
      const blob = await r.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "new-opportunities.csv"; a.click();
      URL.revokeObjectURL(a.href);
      toast.success("New RFPs CSV downloaded");
    } catch (e) { toast.error("Export failed"); }
  };

  useEffect(() => { load(); }, []);

  if (!stats) {
    return (
      <div className="p-8 text-zinc-500" data-testid="dashboard-loading">Loading…</div>
    );
  }

  return (
    <div className="p-8" data-testid="dashboard-page">
      {/* Headline */}
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">
          Control Room · Canada
        </div>
        <h1 className="text-3xl font-bold font-[Chivo] tracking-tight">
          Active procurement opportunities, scored for HR / Payroll / ERP / Oracle relevance.
        </h1>
      </div>

      {/* Recent Crawl Runs */}
      {recentRuns.length > 0 && (
        <div className="mb-6 border border-zinc-200 bg-white">
          <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History size={14} className="text-zinc-500" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Recent crawls</div>
                <div className="text-sm font-semibold font-[Chivo]">Last {recentRuns.length} runs</div>
              </div>
            </div>
            <button
              className="text-xs font-mono uppercase tracking-wider text-[#002FA7] hover:underline"
              onClick={() => navigate("/runs")}
            >
              View all →
            </button>
          </div>
          <div className="divide-y divide-zinc-100">
            {recentRuns.map(r => {
              const isRunning = r.status === "running" || !r.finished_at;
              return (
                <div key={r.id} className="flex items-center gap-4 px-5 py-2.5 text-sm">
                  {isRunning ? (
                    <RefreshCw size={14} className="text-[#002FA7] animate-spin" />
                  ) : r.status === "completed" ? (
                    <CheckCircle size={14} className="text-[#00B140]" />
                  ) : (
                    <XCircle size={14} className="text-[#E00000]" />
                  )}
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono block">Started</span>
                      <span className="font-mono text-xs">{r.started_at ? new Date(r.started_at).toLocaleString("en-CA") : "—"}</span>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono block">Found</span>
                      <span className="font-mono text-xs">{r.opportunities_found} opps</span>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono block">New / Updated</span>
                      <span className="font-mono text-xs">{r.opportunities_new} / {r.opportunities_updated}</span>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono block">Sources</span>
                      <span className="font-mono text-xs">{(r.sources_succeeded || []).join(", ") || "—"}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* New RFPs from latest crawl */}
      {newOpps && (
        <div className="mb-6 border border-zinc-200 bg-white">
          <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-[#002FA7]" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">New RFPs</div>
                <div className="text-sm font-semibold font-[Chivo]">
                  {newOpps.total} new from latest crawl
                  <span className="text-zinc-400 font-normal text-xs ml-2 font-mono">
                    {newOpps.run.finished_at ? new Date(newOpps.run.finished_at).toLocaleString("en-CA") : ""}
                  </span>
                </div>
              </div>
            </div>
            <button
              className="text-xs font-mono uppercase tracking-wider text-[#002FA7] hover:underline inline-flex items-center gap-1"
              onClick={downloadNewCsv}
            >
              <Download size={12} /> Download new as CSV
            </button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                <th className="text-left px-5 py-2 font-medium">Score</th>
                <th className="text-left px-3 py-2 font-medium">Title / Buyer</th>
                <th className="text-left px-3 py-2 font-medium">Platform</th>
                <th className="text-left px-3 py-2 font-medium">Prov</th>
                <th className="text-left px-3 py-2 font-medium">Closing</th>
              </tr>
            </thead>
            <tbody>
              {newOpps.items.slice(0, 20).map((o) => (
                <tr
                  key={o.id}
                  className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                  onClick={() => navigate(`/opportunities/${o.id}`)}
                >
                  <td className="px-5 py-3 align-top"><RelevanceBadge score={o.relevance_score} /></td>
                  <td className="px-3 py-3 align-top">
                    <div className="font-medium text-zinc-900 leading-snug">{o.rfp_title}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{o.buyer_name}</div>
                  </td>
                  <td className="px-3 py-3 align-top text-xs font-mono">{o.source_platform}</td>
                  <td className="px-3 py-3 align-top font-mono text-xs">{o.province}</td>
                  <td className="px-3 py-3 align-top">
                    <FmtDate value={o.closing_deadline} />
                  </td>
                </tr>
              ))}
              {newOpps.items.length > 20 && (
                <tr>
                  <td colSpan={5} className="px-5 py-3 text-center text-xs text-zinc-500 font-mono">
                    + {newOpps.items.length - 20} more — <button className="text-[#002FA7] hover:underline" onClick={() => navigate("/opportunities")}>view all</button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <KPICard
          testId="kpi-total-active"
          label="Active RFPs"
          value={stats.active_count}
          sublabel={`of ${stats.total_opportunities} total tracked`}
          icon={Activity}
        />
        <KPICard
          testId="kpi-high-relevance"
          label="High Relevance (≥70)"
          value={stats.high_relevance_count}
          sublabel="Open + strong HR/ERP/Oracle fit"
          icon={Target}
          accent="#00B140"
        />
        <KPICard
          testId="kpi-closing-week"
          label="Closing ≤ 7 days"
          value={stats.closing_this_week}
          sublabel="Act fast"
          icon={AlertTriangle}
          accent={stats.closing_this_week > 0 ? "#E00000" : "#0A0A0A"}
        />
        <KPICard
          testId="kpi-documents"
          label="Docs Downloaded"
          value={stats.documents_downloaded}
          sublabel="Local archive"
          icon={FileText}
        />
        <KPICard
          testId="kpi-categories"
          label="Categories Tracked"
          value={stats.categories.length}
          sublabel="HR · Payroll · ERP · Oracle · SAP · K-12"
          icon={Database}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top opportunities */}
        <div className="lg:col-span-2 border border-zinc-200 bg-white" data-testid="top-opportunities-card">
          <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                Top opportunities
              </div>
              <div className="text-sm font-semibold font-[Chivo]">
                Highest relevance · open
              </div>
            </div>
            <button
              className="text-xs font-mono uppercase tracking-wider text-[#002FA7] hover:underline"
              onClick={() => navigate("/opportunities")}
              data-testid="view-all-opportunities-btn"
            >
              View all →
            </button>
          </div>
          <table className="w-full text-sm" data-testid="top-opportunities-table">
            <thead className="bg-zinc-50">
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                <th className="text-left px-5 py-2 font-medium">Score</th>
                <th className="text-left px-3 py-2 font-medium">Title / Buyer</th>
                <th className="text-left px-3 py-2 font-medium">Category</th>
                <th className="text-left px-3 py-2 font-medium">Closing</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <tr
                  key={o.id}
                  className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                  onClick={() => navigate(`/opportunities/${o.id}`)}
                  data-testid={`opp-row-${o.rfp_id}`}
                >
                  <td className="px-5 py-3 align-top">
                    <RelevanceBadge score={o.relevance_score} />
                  </td>
                  <td className="px-3 py-3 align-top">
                    <div className="font-medium text-zinc-900 leading-snug">{o.rfp_title}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      <span className="font-mono">{o.source_platform}</span> · {o.buyer_name} · {o.province}
                    </div>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <div className="text-xs">{o.category_primary}</div>
                  </td>
                  <td className="px-3 py-3 align-top">
                    <FmtDate value={o.closing_deadline} />
                    {typeof o.days_remaining === "number" && o.days_remaining >= 0 && (
                      <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                        {o.days_remaining}d remaining
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 align-top">
                    <StatusBadge status={o.status} active={o.active_flag} days={o.days_remaining} />
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-zinc-500 text-sm">
                    No opportunities yet — click "Run Crawl Now".
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Right column: breakdowns */}
        <div className="space-y-6">
          <div className="border border-zinc-200 bg-white" data-testid="categories-card">
            <div className="px-5 py-3 border-b border-zinc-200">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">By Category</div>
              <div className="text-sm font-semibold font-[Chivo]">Active opportunities</div>
            </div>
            <div className="p-5 space-y-3">
              {stats.categories.map(c => {
                const max = Math.max(...stats.categories.map(x => x.count));
                const pct = (c.count / max) * 100;
                return (
                  <div key={c.category}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-zinc-700">{c.category}</span>
                      <span className="font-mono text-zinc-500">{c.count}</span>
                    </div>
                    <div className="h-1 bg-zinc-100">
                      <div className="h-full bg-[#002FA7]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border border-zinc-200 bg-white" data-testid="provinces-card">
            <div className="px-5 py-3 border-b border-zinc-200">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">By Province</div>
              <div className="text-sm font-semibold font-[Chivo] flex items-center gap-2">
                <MapPin size={14} /> Geographic spread
              </div>
            </div>
            <div className="p-5 grid grid-cols-3 gap-3">
              {stats.provinces.map(p => (
                <div key={p.province} className="border border-zinc-200 p-2">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{p.province}</div>
                  <div className="text-xl font-bold font-[Chivo]">{p.count}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-zinc-200 bg-white" data-testid="platforms-card">
            <div className="px-5 py-3 border-b border-zinc-200">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">By Source Platform</div>
              <div className="text-sm font-semibold font-[Chivo]">Active opportunities</div>
            </div>
            <div className="p-5 space-y-2">
              {stats.platforms.map(p => (
                <div key={p.platform} className="flex items-center justify-between text-sm">
                  <span className="font-mono text-zinc-700">{p.platform}</span>
                  <span className="font-mono text-zinc-900 font-semibold">{p.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
